import { useState, useCallback } from 'react';
import AddressActions from 'actions/AddressActions';
import LanguageLocale from 'utils/LanguageLocale';
import helperUtils from 'utils/Helpers';

const { isCountryInternational, isValidCountry } = LanguageLocale;
const { sanitizeAvsField } = helperUtils;

export function useCountryStateManagement({
    isEditMode,
    updateEditStore,
    onCountryChange,
    state,
    setAddress,
    countrySelectRef,
    stateSelectRef,
    displayCityStateInputsprop,
    setIsInternational,
    avsLoqateV2 = false,
    selectedCountry
}) {
    const [stateList, setStateList] = useState([]);
    const [countryInvalid, setCountryInvalid] = useState(false);
    const [displayCityStateInputs, setDisplayCityStateInputs] = useState(displayCityStateInputsprop);
    const [zipCodeInvalid, setZipCodeInvalid] = useState(null);
    const [cityStateZipInvalid, setCityStateZipInvalid] = useState(false);

    const setStateListData = useCallback(
        (states, country) => {
            if (isEditMode) {
                setStateList(states);
                updateEditStore('country', country);

                if (countrySelectRef.current) {
                    countrySelectRef.current.setValue(country);
                }

                if (stateSelectRef.current && state) {
                    stateSelectRef.current.setValue(state);
                    updateEditStore('state', state);
                }
            } else {
                setStateList(states);
                updateEditStore('country', country);

                //TODO: remove countrySelect.setValue once select is fixed
                if (countrySelectRef.current) {
                    countrySelectRef.current.setValue(country);
                }
            }
        },
        [isEditMode, state, updateEditStore]
    );

    const resetCountryAndStateInfo = useCallback(
        (country, stateListData, international) => {
            setStateList(stateListData);
            setIsInternational(international);
            setDisplayCityStateInputs(false);
            setZipCodeInvalid(null);
            setAddress(prev => ({
                ...prev,
                phone: '',
                formattedPhone: ''
            }));
            updateEditStore('country', country);
            updateEditStore('state', '');
            updateEditStore('city', '');
            updateEditStore('postalCode', '');

            if (onCountryChange) {
                onCountryChange(country);
            }
        },
        [updateEditStore, onCountryChange, setStateList, setIsInternational, setDisplayCityStateInputs, setZipCodeInvalid, setAddress]
    );

    const handleCityOnChange = useCallback(
        e => {
            // GIM-5454: strip disallowed special chars from City on input when AVS v2 is on (letters +
            // CA accents + ' . - survive). AVS applies to US/CA only, so international countries keep the
            // legacy pass-through.
            const value = avsLoqateV2 && isValidCountry(selectedCountry) ? sanitizeAvsField('city', e.target.value, selectedCountry) : e.target.value;
            updateEditStore(e.target.name, value);
            setCityStateZipInvalid(null);
        },
        [updateEditStore, avsLoqateV2, selectedCountry]
    );

    const handleStateSelect = useCallback(
        e => {
            // GIM-5454: strip State/Province to letters-only on input when AVS v2 is on. Gated to US/CA
            // (the AVS countries); the international free-text region keeps legacy behavior.
            const value =
                avsLoqateV2 && isValidCountry(selectedCountry) ? sanitizeAvsField('state', e.target.value, selectedCountry) : e.target.value;
            updateEditStore('state', value);
            setCityStateZipInvalid(null);
        },
        [updateEditStore, avsLoqateV2, selectedCountry]
    );

    const handleCountrySelect = useCallback(
        e => {
            const country = e.target.value;
            const international = isCountryInternational(country);

            if (!international) {
                AddressActions.getStateList(country, states => resetCountryAndStateInfo(country, states, international));
            } else {
                resetCountryAndStateInfo(country, null, international);
            }
        },
        [resetCountryAndStateInfo]
    );

    return {
        stateList,
        countryInvalid,
        resetCountryAndStateInfo,
        setStateListData,
        displayCityStateInputs,
        zipCodeInvalid,
        setCountryInvalid,
        setIsInternational,
        setDisplayCityStateInputs,
        setZipCodeInvalid,
        cityStateZipInvalid,
        setCityStateZipInvalid,
        handleCityOnChange,
        handleStateSelect,
        handleCountrySelect
    };
}
